import { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Mail, PlayCircle, Search, TrendingUp, UserCheck, X } from 'react-feather';

import dayjs from 'dayjs';
import { Link, NotFound, Spinner } from '@/components';
import Segmented from '@/components/segmented/Segmented';
import { PaginationNew } from '@/components/pagination-new';
import { useAuth, useAuthUser } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { useCollaboration, useCollaborations, useCollaborationsInvite } from '@/hooks/influencer';
import { throttle } from '@/utils/scrollUtils';
import { updateUserInStorage } from '@/utils/auth';

import { Drawer } from '@/components/drawer/Drawer';
import { ExpandableBox } from '@/components/expandable-box';
import { getFileIcon, PlatformIcon } from '@/utils/platformIcon';
import { message } from 'antd/lib';
import { TextInput } from '@/components/text-input';
import Footer from '@/components/footer/Footer';
import SocialAccountsModal from '../invite/modals/SocialAccountsModal';
import WelcomeModal from '../invite/modals/WelcomeModal';
import { CollaborationItem, CollaborationItemHead } from './components/CollaborationItem';
import styles from './Collaborations.module.css';
import CollabIcon from '../../../public/assets/icons/collaborations.svg';
import ExpiredIcon from '../../../public/assets/icons/expired.svg';
import FinishedIcon from '../../../public/assets/icons/finished.svg';

interface CollaborationsResponseInvite {
  invites: Api.Influencer.CollaborationSearchItem[];
}
interface CollaborationsResponse {
  collaborations: Api.Influencer.CollaborationSearchItem[];
  pageNumber: number;
  pageSize: number;
  countTotal: number;
  hasMore: true;
}
type CollaborationType = 'DECLINED_OR_EXPIRED' | 'ONGOING' | 'DONE';

export function Collaborations() {
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [type, setType] = useState<CollaborationType>('ONGOING');
  const [accountsModal, setAccountsModal] = useState(false);
  const [welcomeModal, setWelcomeModal] = useState(false);
  const [welcomeChecked, setWelcomeChecked] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [collab, setCollab] = useState<null | Api.Influencer.CollaborationSearchItem>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  const [showSearchBar, setShowSearchBar] = useState(() => window.innerWidth > 767);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const { invites, isLoading: isLoadingInvite } = useCollaborationsInvite();

  const { data, isLoading } = useCollaborations({
    currentPage,
    type,
    perPage: itemsPerPage,
    text: debouncedSearchTerm
  });

  const {
    data: collaborationData,
    refetch: fetchCollaboration,
    isLoading: isLoadingCollaboration
  } = useCollaboration({
    id: collab?.collaboration_id as string,
    enabled: false
  });

  const user = useAuthUser();
  const { authData, setAuthData } = useAuth();

  const { t } = useTranslation();

  const topDivRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTop = useRef<number>(0);

  const memoInvites = useMemo(
    () => (invites as CollaborationsResponseInvite)?.invites || [],
    [invites]
  );

  const memoData = useMemo(() => {
    const response = data as CollaborationsResponse;
    return {
      collaborations: response?.collaborations || [],
      pageNumber: response?.pageNumber || 0,
      pageSize: response?.pageSize || 10,
      countTotal: response?.countTotal || 0,
      hasMore: response?.hasMore || false
    };
  }, [data]);
  const memoDataCollabs = useMemo(
    () => (data?.collaborations as Api.Influencer.CollaborationSearchItem[]) || [],
    [memoData]
  );

  useEffect(() => {
    if (!isLoadingInvite && !isLoading) {
      setInitialLoad(false);
    }
  }, [isLoadingInvite, isLoading]);

  useEffect(() => {
    if (!collab?.collaboration_id) return;
    fetchCollaboration();
  }, [collab?.collaboration_id]);

  useEffect(() => {
    if (!welcomeChecked && user?.permissions?.show_popup_first_collaboration) {
      setWelcomeModal(true);
      setWelcomeChecked(true);

      // update user data
      const newData = {
        ...authData.user,
        permissions: {
          ...authData.user.permissions,
          show_popup_first_collaboration: false
        }
      };

      updateUserInStorage(newData);
      setAuthData({ ...authData, user: newData });
    } else if (user?.missing_data?.includes('SOCIAL_ACCOUNTS')) {
      setAccountsModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!topDivRef.current) return;
      const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

      if (topDivRef.current && currentScroll < 150) {
        topDivRef.current.classList.remove('hidden');
        topDivRef.current.dataset.hidden = '0';
        return;
      }

      if (currentScroll > lastScrollTop.current) {
        // scroll to bottom
        topDivRef.current.classList.add('hidden');
        topDivRef.current.dataset.hidden = '1';
      } else {
        // scroll to top
        topDivRef.current.classList.remove('hidden');
        topDivRef.current.dataset.hidden = '0';
      }

      lastScrollTop.current = currentScroll <= 0 ? 0 : currentScroll;
    };

    const throttledHandleScroll = throttle(handleScroll, 100);

    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, []);

  function closeWelcomeModal() {
    setWelcomeModal(false);
    if (user?.missing_data?.includes('SOCIAL_ACCOUNTS')) {
      setAccountsModal(true);
    }
  }

  function updateUserData(newData: Api.UserObject) {
    updateUserInStorage(newData);
    setAuthData({ ...authData, user: newData });
    setAccountsModal(false);
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  if (!user?.is_influencer) return null;

  function handleTypeChange(newType: string) {
    setType(newType as CollaborationType);
    setCurrentPage(0);
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }

  const handleCopy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(link);
      message.success(t('alert.COPIED'));
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      console.error('copy error', error);
    }
  };

  const emptyStateConfig = {
    ONGOING: {
      icon: <CollabIcon />,
      title: <h2>{t('draft.empty.collab.title')}</h2>,
      description: t('draft.empty.collab.subtitle')
    },
    DECLINED_OR_EXPIRED: {
      icon: <ExpiredIcon />,
      title: <h2>{t('draft.empty.expired.title')}</h2>,
      description: <p>{t('draft.empty.expired.subtitle')}</p>
    },
    DONE: {
      icon: <FinishedIcon />,
      title: <h2>{t('draft.empty.finished.title')}</h2>,
      description: <p>{t('draft.empty.finished.subtitle')}</p>
    }
  };

  return (
    <div ref={topDivRef} className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>{t('collaborations.heading')}</h2>
        <div className={styles.segmentedWrap}>
          <Segmented
            value={type}
            options={[
              {
                key: 'ONGOING',
                value: t('collaborations.tabs.active')
              },
              {
                key: 'DECLINED_OR_EXPIRED',
                value: t('collaborations.tabs.declined')
              },
              {
                key: 'DONE',
                value: t('collaborations.tabs.finished')
              }
            ]}
            onChange={handleTypeChange}
          />
        </div>
      </div>
      <div className={styles.segmentedWrapSm}>
        <Segmented
          value={type}
          options={[
            {
              key: 'ONGOING',
              value: t('collaborations.tabs.active')
            },
            {
              key: 'DECLINED_OR_EXPIRED',
              value: t('collaborations.tabs.declined')
            },
            {
              key: 'DONE',
              value: t('collaborations.tabs.finished')
            }
          ]}
          onChange={handleTypeChange}
        />
      </div>

      {initialLoad ? (
        <div className={styles.AuthWrap}>
          <div className={styles.AuthSpinner}>
            <Spinner />
          </div>
        </div>
      ) : !memoInvites.length &&
        !memoDataCollabs.length &&
        authData.code === 'ACCOUNT_REGISTERED' ? (
        <div className={styles.dashboardTitle}>
          <NotFound text={t('collaborations.welcome')} />
          <p className={styles.dashboardDescription}>{t('collaborations.welcomeDescription')}</p>
          <h3 className={styles.dashboardNext}>{t('collaborations.next')}</h3>
          <div className={styles.dashboardContainer}>
            <div className={styles.dashboardStep}>
              <UserCheck size={24} className={styles.dashboardStepIcon} />
              <div className={styles.dashboardStepTitle}>{t('collaborations.step1.title')}</div>
              <div className={styles.dashboardStepDescription}>
                {t('collaborations.step1.description')}
              </div>
            </div>
            <div className={styles.dashboardStep}>
              <Mail size={24} className={styles.dashboardStepIcon} />
              <div className={styles.dashboardStepTitle}>{t('collaborations.step2.title')}</div>
              <div className={styles.dashboardStepDescription}>
                {t('collaborations.step2.description')}
              </div>
            </div>
            <div className={styles.dashboardStep}>
              <PlayCircle size={24} className={styles.dashboardStepIcon} />
              <div className={styles.dashboardStepTitle}>{t('collaborations.step3.title')}</div>
              <div className={styles.dashboardStepDescription}>
                {t('collaborations.step3.description')}
              </div>
            </div>
            <div className={styles.dashboardStep}>
              <TrendingUp size={24} className={styles.dashboardStepIcon} />
              <div className={styles.dashboardStepTitle}>{t('collaborations.step4.title')}</div>
              <div className={styles.dashboardStepDescription}>
                {t('collaborations.step4.description')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {!!memoInvites.length && type === 'ONGOING' && (
            <div className={styles.content}>
              <div className={styles.contentHeader}>
                <h3 className={styles.contentTitle}>
                  {t('collaborations.newInvites')} ({memoInvites.length})
                </h3>
                <CollaborationItemHead type="INVITE" />
              </div>

              {memoInvites.map((collaboration) => (
                <CollaborationItem
                  key={collaboration.collaboration_id}
                  collab={collaboration}
                  type="INVITE"
                  link={`/connect/collaborations/${collaboration.collaboration_id}/invite`}
                />
              ))}
            </div>
          )}
          <div className={styles.content}>
            <div className={styles.contentHeader}>
              <div className={styles.contentTitleWrap}>
                <h3 className={styles.contentTitle}>
                  {type === 'ONGOING' && t('collaborations.ongoing')}
                  {type === 'DECLINED_OR_EXPIRED' && t('collaborations.declined')}
                  {type === 'DONE' && t('collaborations.finished')} ({memoData.countTotal || 0})
                </h3>
                <div
                  className={styles.searchIcon}
                  onClick={() => setShowSearchBar((prev) => !prev)}
                >
                  {!!searchTerm.length && <div className={styles.searchDot} />}
                  <Search size={24} />
                </div>
              </div>
              {showSearchBar && (
                <div className={styles.search}>
                  <TextInput
                    value={searchTerm}
                    placeholder={t('teamBrands.searchCta')}
                    noSpacing
                    prefix={<Search size={18} color="#474E5450" />}
                    suffix={
                      <div className={styles.closeIcon} onClick={() => setSearchTerm('')}>
                        <X size={14} />
                      </div>
                    }
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              )}
              <CollaborationItemHead type={type} />
            </div>

            {isLoading ? (
              <div className={styles.loader}>
                <Spinner />
              </div>
            ) : (
              !!memoData.collaborations.length && (
                <>
                  {memoData.collaborations.map((collaboration) => (
                    <CollaborationItem
                      key={collaboration.collaboration_id}
                      collab={collaboration}
                      type={type}
                      link={
                        type === 'DECLINED_OR_EXPIRED'
                          ? `/connect/collaborations/${collaboration.collaboration_id}/invite`
                          : undefined
                      }
                      onCampaignClick={
                        type === 'ONGOING' || type === 'DONE'
                          ? () => setCollab(collaboration)
                          : undefined
                      }
                    />
                  ))}
                  {!(type === 'ONGOING' && !memoData.hasMore && !memoData.pageNumber) && (
                    <PaginationNew
                      currentPage={currentPage}
                      totalPages={Math.ceil(memoData.countTotal / memoData.pageSize) - 1}
                      maxResult={memoData.countTotal}
                      setPage={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      onItemsPerPageChange={setItemsPerPage}
                    />
                  )}
                </>
              )
            )}
          </div>
          {!isLoading && !memoData.collaborations.length && (
            <div className={styles.emptyState}>
              {emptyStateConfig[type].icon}
              {emptyStateConfig[type].title}
              {emptyStateConfig[type].description}
            </div>
          )}
          <div style={{ marginTop: 'auto' }}>
            <Footer />
          </div>
          {welcomeModal && <WelcomeModal onConfirm={closeWelcomeModal} />}
          {accountsModal && <SocialAccountsModal onNext={updateUserData} />}
          {collab && (
            <Drawer
              theme="gray"
              title={
                <div className={styles.drawerTitle}>
                  <div className={styles.brand}>
                    {collab.brand_logo_url && <img src={collab.brand_logo_url} alt="" />}
                    <div>{collab.brand_name}</div>
                  </div>
                  <div className={styles.campaign}>{collab.campaign_name}</div>
                </div>
              }
              onClose={() => setCollab(null)}
            >
              {isLoadingCollaboration && (
                <div className={styles.AuthWrap}>
                  <div className={styles.AuthSpinner}>
                    <Spinner />
                  </div>
                </div>
              )}
              {collaborationData && (
                <div>
                  {(collaborationData.campaign.brief.description.trim() ||
                    !!collaborationData.campaign.brief.files.length) && (
                    <ExpandableBox
                      title={
                        <div className={styles.briefLabel}>{t('inviteView.campaignBrief')}</div>
                      }
                    >
                      {!!collaborationData.campaign.brief.files.length && (
                        <div className={styles.files}>
                          {collaborationData.campaign.brief.files.map((file) => (
                            <Link key={file.url} href={file.url} target="_blank">
                              <div className={styles.file}>
                                {getFileIcon(file.filename)}
                                {file.filename}
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                      {collaborationData.campaign.brief.description.trim() && (
                        <span
                          className={styles.briefInfo}
                          dangerouslySetInnerHTML={{
                            __html: collaborationData.campaign.brief.description
                          }}
                        />
                      )}
                    </ExpandableBox>
                  )}

                  {collaborationData.beats.map((beat, i) => {
                    const products = Object.entries(beat.products || {}).map(([key, value]) => ({
                      key,
                      value
                    }));
                    return (
                      <ExpandableBox
                        key={beat.beat_id}
                        title={
                          <div className={styles.briefLabel}>
                            <span>
                              {t('inviteView.beat')} {i + 1}
                            </span>
                            <span className={styles.beatLabel}>{beat.beat_name}</span>
                          </div>
                        }
                      >
                        <div className={styles.beatInfo}>
                          <div className={styles.beatInfoRow}>
                            <div className={styles.beatInfoLabel}>{t('inviteView.task')}:</div>
                            <div className={styles.beatInfoContentFlex}>
                              {PlatformIcon(beat.platform)}
                              {products.map(({ key, value }) => (
                                <div key={key} className={styles.platformItem}>
                                  <span>{value}</span> {key}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className={styles.beatInfoRow}>
                            <div className={styles.beatInfoLabel}>
                              {t('inviteView.draftsDate')}:
                            </div>
                            <div className={styles.beatInfoContent}>
                              {dayjs(beat.date_draft).format('YYYY-MM-DD')}
                            </div>
                          </div>
                          {beat.dates_publish && (
                            <div className={styles.beatInfoRow}>
                              <div className={styles.beatInfoLabel}>
                                {t('inviteView.publicationDate')}:
                              </div>
                              <div className={styles.beatInfoContent}>
                                {beat.dates_publish.map((date, index) => (
                                  <span key={date}>
                                    {dayjs(date).format('DD.MM.YYYY')}
                                    {index < beat.dates_publish.length - 1 && (
                                      <span className={styles.beatInfoContentSpan}>
                                        {` ${t('inviteView.or')} `}
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {!!beat.brief.files.length && (
                          <div className={styles.files}>
                            {beat.brief.files.map((file) => (
                              <Link key={file.url} href={file.url} target="_blank">
                                <div className={styles.file}>
                                  {getFileIcon(file.filename)}
                                  {file.filename}
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                        {beat.brief.description.trim() && (
                          <span
                            className={styles.briefInfo}
                            dangerouslySetInnerHTML={{ __html: beat.brief.description }}
                          />
                        )}
                      </ExpandableBox>
                    );
                  })}

                  {!!collaborationData.trackers.length && (
                    <>
                      <div className={styles.links}>{t('inviteView.trackingUrl')}</div>
                      <div className={styles.briefLinks}>
                        {collaborationData.trackers.map((tracker) => (
                          <div key={tracker.tracker_url} className={styles.briefLinkItem}>
                            <a
                              href={tracker.tracker_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.briefLink}
                            >
                              {tracker.tracker_url}
                            </a>
                            <div className={styles.briefLinkIcon}>
                              <button
                                type="button"
                                onClick={() => handleCopy(tracker.tracker_url)}
                                className={styles.briefLinkBtn}
                              >
                                <Copy
                                  size={20}
                                  color={copiedLink === tracker.tracker_url ? 'green' : '#474e54'}
                                />
                              </button>
                              {tracker.tracker_note && (
                                <span style={{ fontSize: '14px', color: '#555' }}>
                                  {tracker.tracker_note}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </Drawer>
          )}
        </>
      )}
    </div>
  );
}
